import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse
} from "@material-ui/core";
import { HashRouter as Router, Switch, Route, Redirect, useHistory, useLocation, useRouteMatch } from "react-router-dom";

import DashboardGerencial from "../DashboardGerencial";
import VentasPeriodo from "./VentasPeriodo";
import HorarioPico from "./HorarioPico";
import MasMenosVendidos from "./MasMenosVendidos";
import FrecuentesNuevos from "./FrecuentesNuevos";
import PromosDescuentos from "./PromosDescuentos";
import RankingProductos from "./RankingProductos";
import BajaRotacion from "./BajaRotacion";
import HistorialCliente from "./HistorialCliente";
import CancelacionesDevoluciones from "./CancelacionesDevoluciones";
import MetodosPago from "./MetodosPago";
import PedidosTransferidos from "./PedidosTransferidos";
import CanceladosAutorizacion from "./CanceladosAutorizacion";
import GastosOperativos from "./GastosOperativos";
import MargenProducto from "./MargenProducto";
import SatisfaccionComentarios from "./SatisfaccionComentarios";
import IncidentesQuejas from "./IncidentesQuejas";
import Conciliacion from "./Conciliacion";


function Placeholder({ title, desc }) {
  return (
    <Paper style={{ padding: 18, borderRadius: 14 }}>
      <Typography variant="h6" style={{ fontWeight: 900 }}>{title}</Typography>
      <Typography variant="body2" style={{ opacity: 0.75, marginTop: 6 }}>{desc}</Typography>
      <Box mt={2} style={{ fontSize: 12, opacity: 0.7 }}>
        Listo para conectar API sin romper tu flujo.
      </Box>
    </Paper>
  );
}

function StatusChip({ status }) {
  const map = {
    ok:      { bg: "#e8f5e9", fg: "#1b5e20", text: "" },
    partial: { bg: "#fff8e1", fg: "#8d6e63", text: "" },
    todo:    { bg: "#ffebee", fg: "#b71c1c", text: "" }
  };
  const s = map[status] || map.todo;
  return (
    <span
      style={{
        fontSize: 11,
        padding: "3px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        fontWeight: 800,
        whiteSpace: "nowrap"
      }}
    >
      {s.text}
    </span>
  );
}

function SectionHeader({ icon, title, subtitle, open, onClick }) {
  return (
    <ListItem
      button
      onClick={onClick}
      style={{
        padding: "12px 14px",
        borderRadius: 12,
        margin: "6px 10px",
        background: open ? "rgba(0,0,0,.06)" : "transparent"
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 12,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,.07)",
          marginRight: 12,
          fontSize: 16
        }}
      >
        {icon}
      </span>

      <ListItemText
        primary={<span style={{ fontWeight: 900, letterSpacing: 0.1 }}>{title}</span>}
        secondary={<span style={{ opacity: 0.72 }}>{subtitle}</span>}
      />

      <span style={{ fontWeight: 900, opacity: 0.6, fontSize: 18 }}>
        {open ? "–" : "+"}
      </span>
    </ListItem>
  );
}

function MenuItem({ active, label, status, onClick }) {
  return (
    <ListItem
      button
      onClick={onClick}
      style={{
        padding: "10px 14px 10px 58px",
        margin: "4px 10px",
        borderRadius: 12,
        background: active ? "rgba(0,0,0,.06)" : "transparent",
        borderLeft: active ? "4px solid #111" : "4px solid transparent"
      }}
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={10}>
            <span style={{ fontWeight: active ? 900 : 700, opacity: active ? 1 : 0.9 }}>
              {label}
            </span>
            <StatusChip status={status} />
          </Box>
        }
      />
    </ListItem>
  );
}

function EstadisticasInner() {
  const history = useHistory();
  const location = useLocation();
  const { path, url } = useRouteMatch();

  const menu = useMemo(() => ([
    {
      key: "ventas",
      title: "Ventas",
      subtitle: "Desempeño comercial",
      icon: "💰",
      items: [
        { key: "resumen", label: "Resumen gerencial", to: `${url}/ventas/resumen`, status: "ok" },
        { key: "periodo", label: "Ventas por período", to: `${url}/ventas/periodo`, status: "ok" },
        { key: "pico", label: "Horario punta (heatmap)", to: `${url}/ventas/horario-pico`, status: "ok" },
        { key: "promos", label: "Promociones y descuentos", to: `${url}/ventas/promos`, status: "todo" },
      ]
    },
    {
      key: "productos",
      title: "Productos",
      subtitle: "Ranking y rotación",
      icon: "🍽️",
      items: [
        { key: "ranking", label: "Ranking por producto", to: `${url}/productos/ranking`, status: "ok" },
        { key: "masmenos", label: "Más y menos vendidos", to: `${url}/productos/mas-menos`, status: "partial" },
        { key: "bajarot", label: "Baja rotación", to: `${url}/productos/baja-rotacion`, status: "ok" },
      ]
    },
    {
      key: "clientes",
      title: "Clientes",
      subtitle: "Fidelización y hábitos",
      icon: "👥",
      items: [
        { key: "fn", label: "Frecuentes y nuevos", to: `${url}/clientes/frecuentes-nuevos`, status: "ok" },
        { key: "hist", label: "Historial por cliente", to: `${url}/clientes/historial`, status: "ok" },

        { key: "inc", label: "Incidentes / quejas", to: `${url}/clientes/incidentes`, status: "todo" },
      ]
    },
    {
      key: "pagos",
      title: "Pagos",
      subtitle: "Métodos y conciliación",
      icon: "💳",
      items: [
        { key: "met", label: "Métodos de pago", to: `${url}/pagos/metodos`, status: "todo" },

      ]
    },
    {
      key: "operacion",
      title: "Operación",
      subtitle: "Control y auditoría",
      icon: "🛠️",
      items: [
        { key: "canc", label: "Cancelaciones y devoluciones", to: `${url}/operacion/cancelaciones`, status: "ok" },
        { key: "trans", label: "Pedidos transferidos", to: `${url}/operacion/transferidos`, status: "todo" },
        { key: "cauta", label: "Cancelados (autorización)", to: `${url}/operacion/cancelados-aut`, status: "todo" },
      ]
    },
    {
      key: "rentabilidad",
      title: "Rentabilidad",
      subtitle: "Costos y margen",
      icon: "📈",
      items: [
        { key: "gastos", label: "Gastos operativos", to: `${url}/rentabilidad/gastos`, status: "todo" },
        { key: "margen", label: "Margen por producto", to: `${url}/rentabilidad/margen`, status: "todo" },
      ]
    },
  ]), [url]);
//         { key: "con", label: "Conciliación", to: `${url}/pagos/conciliacion`, status: "todo" },
//        { key: "sat", label: "Satisfacción y comentarios", to: `${url}/clientes/satisfaccion`, status: "todo" },
  const [open, setOpen] = useState(() => {
    const p = location.pathname;
    const found = menu.find(m => p.includes(`/${m.key}/`));
    return found ? { [found.key]: true } : { ventas: true };
  });

  const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  const go = (to) => history.push(to);

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
        <Box>
          <Typography variant="h5" style={{ fontWeight: 900 }}>Estadísticas</Typography>
          <Typography variant="body2" style={{ opacity: 0.75 }}>
            Reportes por sucursal o consolidados. Menú por categorías (acordeón).
          </Typography>
        </Box>
      </Box>

      <Box mt={2} display="flex" gridGap={14} style={{ alignItems: "stretch" }}>
        {/* SIDEBAR BONITO */}
        <Paper
          style={{
            width: 360,
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 12px 30px rgba(0,0,0,.08)"
          }}
        >
          <Box style={{ padding: 16 }}>
            <Typography style={{ fontWeight: 900 }}>Reportes</Typography>
            <Typography variant="body2" style={{ opacity: 0.7, marginTop: 4 }}>
              Selecciona una categoría y un reporte.
            </Typography>
          </Box>

          <Divider />

          <List style={{ padding: "8px 0" }}>
            {menu.map(section => (
              <React.Fragment key={section.key}>
                <SectionHeader
                  icon={section.icon}
                  title={section.title}
                  subtitle={section.subtitle}
                  open={!!open[section.key]}
                  onClick={() => toggle(section.key)}
                />

                <Collapse in={!!open[section.key]} timeout="auto" unmountOnExit>
                  <Box pb={8}>
                    {section.items.map(it => (
                      <MenuItem
                        key={it.key}
                        label={it.label}
                        status={it.status}
                        active={location.pathname === it.to}
                        onClick={() => go(it.to)}
                      />
                    ))}
                  </Box>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {/* CONTENT */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Switch>
            <Route exact path={path}>
              <Redirect to={`${url}/ventas/resumen`} />
            </Route>

            {/* ✅ tu dashboard existente */}
            <Route path={`${path}/ventas/resumen`} component={DashboardGerencial} />

            {/* placeholders */}
            <Route path={`${path}/ventas/periodo`} component={VentasPeriodo} />

            <Route path={`${path}/ventas/horario-pico`} component={HorarioPico} />

            <Route path={`${path}/ventas/promos`} component={PromosDescuentos} />

            <Route path={`${path}/productos/ranking`} component={RankingProductos} />

            <Route path={`${path}/productos/mas-menos`}  component={MasMenosVendidos} />

            <Route path={`${path}/productos/baja-rotacion`} component={BajaRotacion} />

            <Route path={`${path}/clientes/frecuentes-nuevos`} component={FrecuentesNuevos} />

            <Route path={`${path}/clientes/historial`} component={HistorialCliente} />

            <Route path={`${path}/clientes/satisfaccion`} component={SatisfaccionComentarios} />

            <Route path={`${path}/clientes/incidentes`} component={IncidentesQuejas} />

            <Route path={`${path}/pagos/metodos`} component={MetodosPago} />

            <Route path={`${path}/pagos/conciliacion`} component={Conciliacion} />

            <Route path={`${path}/operacion/cancelaciones`}  component={CancelacionesDevoluciones}/>

            <Route path={`${path}/operacion/transferidos`} component={PedidosTransferidos} />

            <Route path={`${path}/operacion/cancelados-aut`} component={CanceladosAutorizacion}/>

            <Route path={`${path}/rentabilidad/gastos`} component={GastosOperativos} />

            <Route path={`${path}/rentabilidad/margen`} component={MargenProducto} />

            <Route>
              <Placeholder title="No encontrado" desc="Selecciona un reporte del menú de la izquierda." />
            </Route>
          </Switch>
        </Box>
      </Box>
    </Box>
  );
}

export default function EstadisticasLayout() {
  return (
    <Router>
      <EstadisticasInner />
    </Router>
  );
}